/* eslint-disable react/destructuring-assignment */
import { Box, Button, Skeleton, Stack, SxProps, Typography } from '@mui/material'
import React from 'react'
import { FormFieldProps, FormFieldStatus } from './_props'
import { msxs } from '../../../../theme/_utils'
import InformationalTooltip from '../../InformationalTooltip'
import { getFormFieldContainerSx } from '../_utils/utils'
import { statusesProps } from './_status'
import sxs from './_sxs'
import FormFieldLabel from './FormFieldLabel'
import FormFieldVariant from './FormFieldVariant'
import { defaultPalette } from '../../../../theme/idsTheme'

const FormField = <T extends object, O>(props: FormFieldProps<T, O>) => {
    const {
        label = '',
        labelSideComponent = undefined,
        required,
        size = '0.5',
        status = {
            valid: false,
            error: undefined,
        },
        isLoading,
        disabled = false,
        tooltipTitle = '',
        dataCy = '',
        locked,
        sideTooltip,
        sideTooltipProps,
    } = props

    const helperText = 'helperText' in props ? props.helperText : undefined
    const actionButton = 'actionButton' in props ? props.actionButton : undefined

    const isInvalidState = disabled || locked || Boolean(status.error)
    // used to define border opacity
    const isErrorAndNotDisabled = Boolean(status.error) && !disabled
    const errorMessage = status?.error && typeof status.error !== 'boolean' ? status.error.message : undefined

    const containerSx = getFormFieldContainerSx(size)

    const inputStatus: FormFieldStatus = React.useMemo(() => {
        if (status?.error) return 'ERROR'
        if (status?.valid) return 'VALID'
        if (status?.warning) return 'WARNING'
        return 'IDLE'
    }, [status])

    const statusProps = statusesProps[inputStatus]

    const boxSxs = {
        ...sxs.FormFieldWrapper,
        ...containerSx,
        ...statusProps.classes?.input,
    }

    if (isLoading)
        return (
            <Box sx={boxSxs}>
                <Skeleton variant="rounded" width="100%" height="3.5rem" />
            </Box>
        )

    const clearable = locked || disabled ? false : props.clearable

    const invalidStateSx: SxProps = {
        opacity: isErrorAndNotDisabled ? 1 : 0.54,
        '& .endAdornmentLocked': {
            '> svg': {
                opacity: 1,
                color: `${defaultPalette.typography.main}`,
            },
        },
    }

    return (
        <Stack id={props?.id} data-cy={dataCy} sx={boxSxs} flexDirection="column">
            {status && label && (
                <FormFieldLabel
                    label={label}
                    labelSideComponent={labelSideComponent}
                    status={status}
                    disabled={locked || disabled}
                    tooltipTitle={tooltipTitle}
                    required={required}
                />
            )}
            <Box sx={msxs(sxs.FormFieldContentHorizontal, sxs.FormFieldContent)}>
                <Box sx={isInvalidState ? invalidStateSx : undefined}>
                    <FormFieldVariant {...props} clearable={clearable} />
                </Box>
                {sideTooltip && (
                    <InformationalTooltip
                        withMarginRight
                        disableFocusListener
                        title={sideTooltip}
                        {...sideTooltipProps}
                    />
                )}
            </Box>
            {helperText && (
                <Stack
                    sx={{
                        ...sxs.FormFieldHelperTextContainer,
                        width: sideTooltip ? 'calc(100% - 2.35rem)' : '100%',
                    }}
                >
                    <Typography sx={statusProps.classes?.helperText}>{helperText}</Typography>
                </Stack>
            )}
            {errorMessage && (
                <Stack justifyContent="flex-start" sx={sxs.FormFieldErrorContainer}>
                    <Typography sx={sxs.FormFieldErrorContainerText}>{errorMessage}</Typography>
                </Stack>
            )}
            <Box sx={sxs.FormFieldActionButtonContainer}>
                {actionButton && (
                    <Button
                        sx={sxs.FormFieldActionButton}
                        startIcon={actionButton?.icon}
                        onClick={() => (actionButton?.action ? actionButton.action() : undefined)}
                    >
                        {actionButton?.label}
                    </Button>
                )}
            </Box>
        </Stack>
    )
}

export default FormField
